import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useMatch, useNavigate, useParams } from "react-router-dom";

import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import {
  getVehicleCapacitiesMasterEdit,
  storeVehicleCapacitiesMaster,
  updateVehicleCapacitiesMaster
} from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

const FreightVehicleCapacitiesFormPage = () => {
  const navigate = useNavigate();
  const newMatch = useMatch({ path: "/freight/vehicle_capacities/new", end: true });
  const { id: routeId } = useParams();
  const isNew = Boolean(newMatch);
  const masterId = isNew ? null : routeId;

  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "Vehicle capacities", link: "/freight/vehicle_capacities" },
      { label: isNew ? "New capacity" : `Edit #${masterId ?? ""}` }
    ],
    [roleLabel, isNew, masterId]
  );

  const [loading, setLoading] = useState(!isNew);
  const [submitting, setSubmitting] = useState(false);
  const [minLoad, setMinLoad] = useState("");
  const [maxLoad, setMaxLoad] = useState("");
  const [status, setStatus] = useState("1");

  const loadEdit = useCallback(async () => {
    if (!masterId) return;
    setLoading(true);
    try {
      const res = await getVehicleCapacitiesMasterEdit(masterId);
      const d = res?.data?.vehicleCapacityData ?? res?.data?.vehicleCapacity ?? res?.data;
      if (!d) throw new Error("Not found");
      setMinLoad(d.min_load != null ? String(d.min_load) : "");
      setMaxLoad(d.max_load != null ? String(d.max_load) : "");
      setStatus(String(d.status ?? "1"));
    } catch (e) {
      showError(e.message || "Load failed");
      navigate("/freight/vehicle_capacities", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [masterId, navigate]);

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }
    loadEdit();
  }, [isNew, loadEdit]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (minLoad === "" || maxLoad === "") {
      showError("Both min load and max load are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        min_load: Number(minLoad),
        max_load: Number(maxLoad),
        status: Number(status)
      };
      if (isNew) {
        await storeVehicleCapacitiesMaster(payload);
        showSuccess("Vehicle capacity created");
      } else {
        await updateVehicleCapacitiesMaster({
          id: Number(masterId),
          ...payload
        });
        showSuccess("Vehicle capacity updated");
      }
      navigate("/freight/vehicle_capacities");
    } catch (err) {
      showError(err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout breadcrumbs={breadcrumbs}>
        <div className="max-w-[720px] card offers-table p-6">Loading…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[720px]">
        <div className="card offers-table border-l-4 border-l-[#064E8D]">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className="m-0">{isNew ? "New vehicle capacity" : "Edit vehicle capacity"}</h3>
            <Link to="/freight/vehicle_capacities" className="btn-outline no-underline text-sm">
              ← Back to list
            </Link>
          </div>

          <form className="freight-admin-form space-y-4" onSubmit={onSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min load (tonnes) *</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={minLoad}
                  onChange={(e) => setMinLoad(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max load (tonnes) *</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={maxLoad}
                  onChange={(e) => setMaxLoad(e.target.value)}
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Saving…" : isNew ? "Create" : "Save changes"}
              </button>
              <Link to="/freight/vehicle_capacities" className="btn-outline no-underline inline-flex items-center">
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightVehicleCapacitiesFormPage;
